import { TStoreState } from 'redux/types';
import { createSelector } from '@reduxjs/toolkit';
import {
	TEmAssignmentResponsePayload,
	TFetchContractDocumentResponsePayload,
	TFetchNotificationSubscriptionsResponsePayload,
	TFetchUsersResponsePayload,
	TGetContractDocumentsResponsePayload,
	TGetDownloadedDocumentsResponsePayload,
	T2FAStatusResponsePayload,
	TSettingsStore,
	TSpecialSchemesResponsePayload,
	TIntegrationsResponsePayload,
	TGetHmrcOauthUrlResponsePayload,
	TUploadedSalesReportsResponsePayload,
	TUploadSalesReportResponsePayload,
	TCheckHmrcAvailabilityResponsePayload,
	TCreateDashboardUserResponsePayload,
	TSupportedCountryListResponsePayload,
	TAllCountriesResponsePayload,
	TCountryOption,
} from './types';

export const getSettingsState = (state: TStoreState): TSettingsStore => state.settings;

export const getSettings = getSettingsState;

// ==========================================:

export const getEmAssignment = createSelector(
	[getSettings],
	(user: TSettingsStore): TEmAssignmentResponsePayload => user.emAssignment,
);

// ==========================================:

export const getEmAssignmentLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.emAssignmentLoader,
);

// ==========================================:

export const getContractDocuments = createSelector(
	[getSettings],
	(user: TSettingsStore): TGetContractDocumentsResponsePayload => user.contractDocuments,
);

// ==========================================:

export const getContractDocumentsLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.contractDocumentsLoader,
);

// ==========================================:

export const getDownloadedDocuments = createSelector(
	[getSettings],
	(user: TSettingsStore): TGetDownloadedDocumentsResponsePayload => user.downloadedDocuments,
);

// ==========================================:

export const getDownloadedDocumentsLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.downloadedDocumentsLoader,
);

// ==========================================:

export const getContractDocument = createSelector(
	[getSettings],
	(user: TSettingsStore): TFetchContractDocumentResponsePayload => user.contractDocument,
);

// ==========================================:

export const getContractDocumentLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.contractDocumentLoader,
);

// ==========================================:

export const getNotificationSettings = createSelector(
	[getSettings],
	(user: TSettingsStore): TFetchUsersResponsePayload => user.notificationSettings,
);

// ==========================================:

export const getNotificationSettingsLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.notificationSettingsLoader,
);

// ==========================================:

export const getCreateDashboardUser = createSelector(
	[getSettings],
	(user: TSettingsStore): TCreateDashboardUserResponsePayload => user.createDashboardUser,
);

// ==========================================:

export const getCreateDashboardUserLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.createDashboardUserLoader,
);

// ==========================================:

export const getCreateDashboardUserError = createSelector(
	[getSettings],
	(user: TSettingsStore): string | null => user.createDashboardUserError,
);

// ==========================================:

export const getNeedConfirmAccessCreateDashboardUser = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.needConfirmAccessCreateDashboardUser,
);

// ==========================================:

export const getNotificationSubscriptions = createSelector(
	[getSettings],
	(user: TSettingsStore): TFetchNotificationSubscriptionsResponsePayload =>
		user.notificationSubscriptions,
);

// ==========================================:

export const getNotificationSubscriptionsLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.notificationSubscriptionsLoader,
);

// ==========================================:

export const getPasswordConfirmedLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.passwordConfirmedLoader,
);

// ==========================================:

export const getPasswordConfirmedError = createSelector(
	[getSettings],
	(user: TSettingsStore): string => user.passwordConfirmedError,
);

// ==========================================:

export const getResetCredentials = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.resetCredentials,
);

// ==========================================:

export const getResetCredentialsLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.resetCredentialsLoader,
);

// ==========================================:

export const getPasswordConfirmed2FA = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.passwordConfirmed2FA,
);

// ==========================================:

export const getStatus2FA = createSelector(
	[getSettings],
	(user: TSettingsStore): T2FAStatusResponsePayload => user.status2FA,
);

// ==========================================:

export const getStatus2FALoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.status2FALoader,
);

// ==========================================:

export const getEnable2FALoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.enable2FALoader,
);

// ==========================================:

export const getEnable2FAError = createSelector(
	[getSettings],
	(user: TSettingsStore): string => user.enable2FAError,
);

// ==========================================:

export const getEnable2FA = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.enable2FA,
);

// ==========================================:

export const getDisable2FALoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.disable2FALoader,
);

// ==========================================:

export const getDisable2FAError = createSelector(
	[getSettings],
	(user: TSettingsStore): string => user.disable2FAError,
);

// ==========================================:

export const getDisable2FA = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.disable2FA,
);

// ==========================================:

export const getSpecialSchemes = createSelector(
	[getSettings],
	(user: TSettingsStore): TSpecialSchemesResponsePayload => user.specialSchemes,
);

// ==========================================:

export const getSpecialSchemesLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.specialSchemesLoader,
);

// ==========================================:

export const getIntegrations = createSelector(
	[getSettings],
	(user: TSettingsStore): TIntegrationsResponsePayload => user.integrations,
);

// ==========================================:

export const getIntegrationsLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.integrationsLoader,
);

// ==========================================:

export const getNewWixIntegrationLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.newWixIntegrationLoader,
);

// ==========================================:

export const getNewWixIntegrationError = createSelector(
	[getSettings],
	(user: TSettingsStore): string | null => user.newWixIntegrationError,
);

// ==========================================:

export const getNewSquarespaceIntegrationLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.newSquarespaceIntegrationLoader,
);

// ==========================================:

export const getNewSquarespaceIntegrationError = createSelector(
	[getSettings],
	(user: TSettingsStore): string | null => user.newSquarespaceIntegrationError,
);

// ==========================================:

export const getNewStripeIntegrationLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.newStripeIntegrationLoader,
);

// ==========================================:

export const getNewStripeIntegrationError = createSelector(
	[getSettings],
	(user: TSettingsStore): string | null => user.newStripeIntegrationError,
);

// ==========================================:

export const getHmrcOauthUrl = createSelector(
	[getSettings],
	(user: TSettingsStore): TGetHmrcOauthUrlResponsePayload => user.hmrcOauthUrl,
);

// ==========================================:

export const getHmrcOauthUrlLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.hmrcOauthUrlLoader,
);

// ==========================================:

export const getHmrcOauthUrlError = createSelector(
	[getSettings],
	(user: TSettingsStore): string | null => user.hmrcOauthUrlError,
);

// ==========================================:

export const getHmrcAvailability = createSelector(
	[getSettings],
	(user: TSettingsStore): TCheckHmrcAvailabilityResponsePayload => user.hmrcAvailability,
);

// ==========================================:

export const getHmrcAvailabilityLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.hmrcAvailabilityLoader,
);

// ==========================================:

export const getSupportedCountryList = createSelector(
	[getSettings],
	(user: TSettingsStore): TSupportedCountryListResponsePayload => user.supportedCountryList,
);

export const getSupportedCountryListLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.supportedCountryListLoader,
);

// ==========================================:

export const getAllCountries = createSelector(
	[getSettings],
	(user: TSettingsStore): TAllCountriesResponsePayload => user.allCountries,
);

export const getAllCountriesLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.allCountriesLoader,
);

// ==========================================:

// Codes used by EAS fiscal data that are not part of the ISO alpha-2 list returned by the API.
const NON_ISO_COUNTRIES: TCountryOption[] = [
	{ code: 'XI', label: 'Northern Ireland' },
	{ code: 'EL', label: 'Greece' },
];

export const getAllCountriesOptions = createSelector(
	[getAllCountries],
	(countries: TAllCountriesResponsePayload): TCountryOption[] => {
		const options = (countries ?? []).map((country) => ({
			code: country.isoAlpha2,
			label: country.name,
		}));
		const knownCodes = new Set(options.map((option) => option.code));

		return [...options, ...NON_ISO_COUNTRIES.filter((option) => !knownCodes.has(option.code))].sort(
			(a, b) => a.label.localeCompare(b.label),
		);
	},
);

export const getUploadedSalesReports = createSelector(
	[getSettings],
	(user: TSettingsStore): TUploadedSalesReportsResponsePayload => user.uploadedSalesReports,
);

// ==========================================:

export const getUploadedSalesReportsLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.uploadedSalesReportsLoader,
);

// ==========================================:

export const getUploadSalesReport = createSelector(
	[getSettings],
	(user: TSettingsStore): TUploadSalesReportResponsePayload => user.uploadSalesReport,
);

// ==========================================:

export const getUploadSalesReportLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.uploadSalesReportLoader,
);

// ==========================================:

export const getDownloadSalesReportLoader = createSelector(
	[getSettings],
	(user: TSettingsStore): boolean => user.downloadSalesReportLoader,
);
